import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ReactMarkdown from 'react-markdown';
import { Clock, ArrowLeft } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import ToolCallout from '../components/ToolCallout';
import { Skeleton, SkeletonAvatar } from '../components/Skeleton';
import ShareButtons from '../components/ShareButtons';
import LegalDisclaimer from '../components/LegalDisclaimer';
import { trackArticleRead, trackRelatedClick } from '../lib/analytics';

const LEGAL_CATEGORIES = new Set(['visas', 'immigration', 'legal']);
const MEDICAL_CATEGORIES = new Set(['healthcare', 'medical', 'insurance']);

// iter 136 - Pick which free tool to surface inside an article body.
// Visa / immigration / legal      → Visa Finder
// Cost / city / housing           → Cost Calculator
// Checklist / how-to-move slugs   → Readiness Score (matched by slug,
//                                   since these may live under broader categories)
const COST_CATEGORIES = new Set(['cost', 'cost-of-living', 'housing', 'real-estate', 'cities', 'city']);
const READINESS_SLUG_PREFIXES = ['moving-to-mexico-checklist', 'how-to-move-to-mexico'];

const toolVariantForArticle = (article) => {
  if (!article) return null;
  const slug = (article.slug || '').toLowerCase();
  if (READINESS_SLUG_PREFIXES.some((p) => slug.startsWith(p))) return 'readiness';
  const cat = (article.category || '').toLowerCase();
  if (LEGAL_CATEGORIES.has(cat)) return 'visa';
  if (COST_CATEGORIES.has(cat)) return 'cost';
  return null;
};

const disclaimerVariantForCategory = (category) => {
  const c = (category || '').toLowerCase();
  const isLegal = LEGAL_CATEGORIES.has(c);
  const isMedical = MEDICAL_CATEGORIES.has(c);
  if (isLegal && isMedical) return 'both';
  if (isLegal) return 'legal';
  if (isMedical) return 'medical';
  return null;
};

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const SITE_URL = 'https://mundalo.com';

// ============== AUTHOR BYLINE ==============
// Renders the resolved-author dict from the backend. Links to the author
// profile page when the author has a slug other than the generic editorial.
const AuthorByline = ({ author }) => {
  const a = typeof author === 'object' && author ? author : { name: author || 'Mundalo Editorial' };
  const slug = a.slug || '';
  const initial = (a.name || 'M').trim().charAt(0).toUpperCase();
  const isLinkable = slug && slug !== 'mundalo-editorial';
  const inner = (
    <>
      {a.photo_url ? (
        <img
          src={a.photo_url}
          alt={a.name || ''}
          className="w-10 h-10 rounded-full object-cover bg-[#F4ECD8]"
          data-testid="article-author-photo"
        />
      ) : (
        <div className="w-10 h-10 rounded-full bg-[#0A1F2E] text-[#F9F6EF] flex items-center justify-center font-display italic text-lg">
          {initial}
        </div>
      )}
      <div>
        <p className="text-sm font-medium text-[#0A1F2E]" data-testid="article-author-name">
          {a.name || 'Mundalo Editorial'}
        </p>
        <p className="text-xs text-[#0A1F2E]/55" data-testid="article-author-role">
          {a.role || 'Mundalo Editorial Team'}
        </p>
      </div>
    </>
  );
  if (isLinkable) {
    return (
      <Link
        to={`/relo-news/author/${slug}`}
        className="flex items-center gap-3 group hover:opacity-80 transition"
        data-testid="article-author-link"
      >
        {inner}
      </Link>
    );
  }
  return (
    <div className="flex items-center gap-3" data-testid="article-author-byline">
      {inner}
    </div>
  );
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric', timeZone: 'America/Chicago'});
  } catch {
    return '';
  }
};

// ============== ARTICLE BODY (markdown) ==============
const ArticleBody = ({ markdown }) => (
  <div
    className="prose prose-lg max-w-none font-body text-[#0A1F2E]/90"
    data-testid="article-body"
  >
    <ReactMarkdown
      components={{
        h1: ({ children }) => (
          <h2 className="font-display italic text-3xl md:text-4xl text-[#0A1F2E] mt-12 mb-4">
            {children}
          </h2>
        ),
        h2: ({ children }) => (
          <h2 className="font-display italic text-3xl md:text-4xl text-[#0A1F2E] mt-12 mb-4">
            {children}
          </h2>
        ),
        h3: ({ children }) => (
          <h3 className="font-display italic text-2xl text-[#0A1F2E] mt-8 mb-3">
            {children}
          </h3>
        ),
        p: ({ children }) => (
          <p className="text-[#0A1F2E]/85 leading-[1.75] text-lg mb-6">
            {children}
          </p>
        ),
        ul: ({ children }) => (
          <ul className="list-disc pl-6 mb-6 space-y-2 text-[#0A1F2E]/85 text-lg leading-[1.7]">
            {children}
          </ul>
        ),
        ol: ({ children }) => (
          <ol className="list-decimal pl-6 mb-6 space-y-2 text-[#0A1F2E]/85 text-lg leading-[1.7]">
            {children}
          </ol>
        ),
        li: ({ children }) => <li className="pl-1">{children}</li>,
        strong: ({ children }) => (
          <strong className="text-[#0A1F2E] font-semibold">{children}</strong>
        ),
        em: ({ children }) => <em className="italic">{children}</em>,
        a: ({ href, children }) => (
          <a
            href={href}
            target={href?.startsWith('http') ? '_blank' : undefined}
            rel="noopener noreferrer"
            className="text-[#1A5C6E] underline decoration-1 underline-offset-2 hover:text-[#2CA8A0] transition"
          >
            {children}
          </a>
        ),
        blockquote: ({ children }) => (
          <blockquote className="border-l-4 border-[#E8A830] pl-5 my-8 italic font-display text-2xl text-[#0A1F2E]/85">
            {children}
          </blockquote>
        ),
      }}
    >
      {markdown || ''}
    </ReactMarkdown>
  </div>
);

// ============== RELATED CARD ==============
const RelatedCard = ({ article, fromSlug }) => (
  <Link
    to={`/relo-news/${article.slug}`}
    onClick={() =>
      trackRelatedClick({
        fromSlug,
        toSlug: article.slug,
        category: article.category,
      })
    }
    className="group block"
    data-testid={`related-${article.slug}`}
  >
    <div className="aspect-[4/3] overflow-hidden bg-[#F4ECD8] mb-4">
      <img
        src={article.hero_image_url}
        alt=""
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        loading="lazy"
      />
    </div>
    <span className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#E8A830] font-semibold">
      {article.category_label}
    </span>
    <h3 className="font-display italic text-2xl text-[#0A1F2E] leading-tight mt-2 group-hover:text-[#1A5C6E] transition">
      {article.title}
    </h3>
    <p className="text-sm text-[#0A1F2E]/65 font-body mt-2 line-clamp-2">
      {article.excerpt}
    </p>
    <div className="flex items-center gap-2 mt-3 text-xs text-[#0A1F2E]/55">
      <span>{formatDate(article.published_at)}</span>
      <span className="w-1 h-1 rounded-full bg-[#0A1F2E]/25" />
      <span className="flex items-center gap-1">
        <Clock className="w-2.5 h-2.5" /> {article.read_time}
      </span>
    </div>
  </Link>
);

// ============== MAIN ==============
const ReloNewsArticle = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null);
    setNotFound(false);
    window.scrollTo(0, 0);
    fetch(`${API_URL}/api/news/${encodeURIComponent(slug)}`)
      .then(async (r) => {
        if (r.status === 404) { setNotFound(true); return null; }
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((d) => {
        if (d) {
          setData(d);
          trackArticleRead({ slug: d.slug, category: d.category });
        }
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#F9F6EF] flex flex-col">
        <SEO title="Article Not Found" path={`/relo-news/${slug}`} />
        <Header />
        <main className="flex-1 max-w-4xl mx-auto px-6 md:px-10 py-24 text-center">
          <p className="font-display italic text-5xl text-[#0A1F2E]/60 mb-4">404</p>
          <h1 className="font-display italic text-3xl text-[#0A1F2E] mb-4">
            We couldn't find that article.
          </h1>
          <p className="text-[#0A1F2E]/65 mb-8">
            It may have been retired or the URL changed.
          </p>
          <Link
            to="/relo-news"
            className="inline-flex items-center gap-2 font-mono uppercase text-[11px] tracking-[0.12em] font-semibold bg-[#0A1F2E] text-[#F9F6EF] px-5 py-2.5 rounded-full hover:brightness-110 transition"
            data-testid="article-back-to-news"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Relo News
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#F9F6EF]">
        <Header />
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-12">
          <Skeleton className="h-3 w-32 mb-4" />
          <Skeleton className="h-12 w-full mb-3" />
          <Skeleton className="h-12 w-3/4 mb-6" />
          <div className="flex items-center gap-3 mb-8">
            <SkeletonAvatar size={36} />
            <Skeleton className="h-3 w-40" />
          </div>
          <Skeleton className="aspect-[16/9] w-full mb-10" />
          <div className="space-y-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-5/6" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const a = data.article;
  const related = data.related || [];

  // iter 144k - Article JSON-LD per spec. Single hardcoded author
  // (Francisco Rizo → /about) and Mundalo publisher with the
  // public logo asset. dateModified falls back to datePublished when
  // the article row has never been edited post-publish.
  const articleUrl = `${SITE_URL}/relo-news/${a.slug}`;
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: a.title,
    description: a.seo_description || a.excerpt,
    datePublished: a.published_at,
    dateModified: a.updated_at || a.published_at,
    author: {
      '@type': 'Person',
      name: 'Francisco Rizo',
      url: 'https://mundalo.com/about',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Mundalo',
      url: 'https://mundalo.com',
      logo: {
        '@type': 'ImageObject',
        url: 'https://res.cloudinary.com/des9fk8et/image/upload/e_trim,f_auto,q_auto:good/mundalo/brand/mundalo-logo-green.png',
      },
    },
    url: articleUrl,
    mainEntityOfPage: articleUrl,
    ...(a.hero_image_url
      ? { image: { '@type': 'ImageObject', url: a.hero_image_url } }
      : {}),
  };

  return (
    <div className="min-h-screen bg-[#F9F6EF]" style={{ animation: 'fadeIn 0.4s ease-out' }}>
      <SEO
        title={a.seo_title || a.title}
        description={a.seo_description || a.excerpt}
        path={`/relo-news/${a.slug}`}
        image={a.hero_image_url}
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
      </Helmet>

      <Header />

      <article className="pb-20">
        {/* Article hero */}
        <header className="max-w-4xl mx-auto px-6 md:px-10 pt-12 md:pt-16 pb-10">
          <Link
            to="/relo-news"
            className="inline-flex items-center gap-1.5 font-mono uppercase text-[11px] tracking-[0.15em] text-[#1A5C6E] hover:text-[#2CA8A0] mb-8 transition"
            data-testid="article-back-link"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Relo News
          </Link>

          <div className="flex items-center gap-3 mb-6">
            <span
              className="font-mono uppercase text-[10px] tracking-[0.18em] bg-[#E8A830] text-[#0A1F2E] px-2.5 py-1 rounded-full font-semibold"
              data-testid="article-category"
            >
              {a.category_label}
            </span>
            <span className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/60">
              {formatDate(a.published_at)}
            </span>
            <span className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/60 flex items-center gap-1">
              <Clock className="w-3 h-3" /> {a.read_time}
            </span>
          </div>

          <h1
            className="font-display italic text-4xl sm:text-5xl md:text-6xl text-[#0A1F2E] leading-[1.05] mb-6"
            data-testid="article-title"
          >
            {a.title}
          </h1>

          <p className="font-body text-xl text-[#0A1F2E]/70 leading-relaxed max-w-3xl mb-8">
            {a.excerpt}
          </p>

          <div className="flex flex-wrap items-center justify-between gap-4 pt-6 border-t border-[#0A1F2E]/10">
            <AuthorByline author={a.author} />
            <ShareButtons title={a.title} slug={a.slug} />
          </div>
        </header>

        {/* Hero image */}
        {a.hero_image_url && (
          <div className="max-w-6xl mx-auto px-6 md:px-10 mb-12">
            <div className="aspect-[16/9] overflow-hidden bg-[#F4ECD8]">
              <img
                src={a.hero_image_url}
                alt=""
                className="w-full h-full object-cover"
                data-testid="article-hero-image"
              />
            </div>
          </div>
        )}

        {/* Body */}
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          {disclaimerVariantForCategory(a.category) && (
            <LegalDisclaimer variant={disclaimerVariantForCategory(a.category)} />
          )}
          {/* iter 136 - Tool callout above body for visa / cost / checklist articles */}
          {toolVariantForArticle(a) && (
            <ToolCallout variant={toolVariantForArticle(a)} testId={`article-tool-callout-${toolVariantForArticle(a)}`} />
          )}
          <ArticleBody markdown={a.body_markdown} />
        </div>
      </article>

      {/* Related articles */}
      {related.length > 0 && (
        <section
          className="bg-white border-t border-[#0A1F2E]/8 py-20"
          data-testid="related-articles-section"
        >
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <h2 className="font-display italic text-3xl md:text-4xl text-[#0A1F2E] mb-2">
              Keep reading
            </h2>
            <p className="text-[#0A1F2E]/60 mb-10 font-body">
              {related.every((r) => r.category === a.category)
                ? `More from ${a.category_label}`
                : 'Stories you might like next'}
            </p>
            <div className={`grid grid-cols-1 ${related.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-3'} gap-x-8 gap-y-12`}>
              {related.map((r) => <RelatedCard key={r.slug} article={r} fromSlug={a.slug} />)}
            </div>
          </div>
        </section>
      )}

      <Footer />

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default ReloNewsArticle;
